/**
 * React hook for PostHog tracking integration
 * Handles automatic identity management and tenant context
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/_core/hooks/useAuth';
import { trpc } from '@/lib/trpc';
import {
  initTenantContext,
  identifyUser,
  resetIdentity,
  trackUserLogin,
  trackEvent,
  isTrackingReady,
  EVENTS,
} from '@/lib/tracking';

/**
 * Hook to initialize tracking with tenant context and user identity
 * Should be called once at the app root level
 */
export function useTrackingInit(): void {
  const { user, loading: isLoading } = useAuth();
  const { data: tenantConfig } = trpc.config.getTenantConfig.useQuery();
  const hasInitialized = useRef(false);
  const hasIdentified = useRef(false);
  const previousUserId = useRef<string | null>(null);

  // Initialize tenant context when config is loaded
  useEffect(() => {
    if (tenantConfig && !hasInitialized.current) {
      // Extract tenant_slug from the current domain
      // e.g., "physio-schwabach" from "physio-schwabach.kursivia.de"
      const hostname = window.location.hostname;
      let tenantSlug = 'unknown';
      
      // Check if it's a kursivia.de subdomain
      if (hostname.endsWith('.kursivia.de')) {
        tenantSlug = hostname.split('.')[0]; // Get subdomain
      } else if (!hostname.includes('localhost') && !hostname.includes('127.0.0.1') && !hostname.includes('manus')) {
        // Custom domain - use full hostname as slug
        tenantSlug = hostname.replace(/\./g, '-');
      }
      
      // tenant_id is unknown for now (no tenantId system yet)
      initTenantContext(tenantSlug, undefined);
      hasInitialized.current = true;
    }
  }, [tenantConfig]);

  // Handle user identity
  useEffect(() => {
    if (isLoading) return;

    // User logged out
    if (!user && previousUserId.current) {
      resetIdentity();
      hasIdentified.current = false;
      previousUserId.current = null;
      return;
    }

    // User logged in
    if (user && !hasIdentified.current) {
      // Use openId as the Auth0 sub equivalent
      const userId = user.openId || user.id?.toString();
      
      if (userId) {
        // Check if user is the deployment owner by comparing openId with OWNER_OPEN_ID
        const ownerOpenId = tenantConfig?.ownerOpenId;
        const isOwner = ownerOpenId ? user.openId === ownerOpenId : false;
        
        identifyUser(
          userId,
          user.email || '',
          user.role,
          isOwner
        );
        
        // Track login event (only on first identification)
        if (!previousUserId.current) {
          trackUserLogin('auth0');
        }
        
        hasIdentified.current = true;
        previousUserId.current = userId;
      }
    }
  }, [user, isLoading, tenantConfig]);
}

/**
 * Hook to track page views with additional context
 * PostHog handles automatic pageviews, but this can be used for custom tracking
 */
export function usePageTracking(pageName: string, properties?: Record<string, any>): void {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current && isTrackingReady()) {
      trackEvent('page_viewed', {
        page_name: pageName,
        ...properties,
      });
      hasTracked.current = true;
    }
  }, [pageName, properties]);
}

/**
 * Hook that returns tracking functions for use in components
 */
export function useTracking() {
  return {
    trackEvent,
    isReady: isTrackingReady,
    EVENTS,
  };
}

export default useTracking;
